import { useState } from 'react';
import { CreatePositionFillRequestDrawer, POSITION_FILL_REQUEST_CREATE } from 'domain/shared/positionFillRequest';
import { useAuthorization } from 'framework/authorization';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { useParams } from 'react-router-dom';
import {
  POSITION_SLOT_ASSIGN,
  POSITION_SLOT_FREEZE,
  POSITION_SLOT_RETIRE,
  POSITION_SLOT_UNFREEZE,
  POSITION_SLOT_UPDATE,
} from '../constants';
import { PositionSlotAssignmentDrawer, PositionSlotFormDrawer } from '../createupdate';
import usePositionSlotsService from '../usePositionSlotsService';
import { PositionSlotValidationProvider } from '../List/PositionSlotValidationContext';
import PositionSlotDetailsContent from './details/PositionSlotDetailsContent';
import usePositionSlotDetails from './model/usePositionSlotDetails';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';

enum ActiveDrawer {
  EDIT = 'edit',
  ASSIGN = 'assign',
  PFR = 'pfr',
}

const PositionSlotDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { isFeatureEnabled } = useFeatureToggle();
  const { isGranted } = useAuthorization();
  const { addMessage } = useMessageService();
  const service = usePositionSlotsService();
  const { positionSlot, isLoading, error, reload } = usePositionSlotDetails(id);
  const [activeDrawer, setActiveDrawer] = useState<Nullable<ActiveDrawer>>(null);

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action();
      addMessage(new SuccessMessage(successMessage));
      await reload();
    } catch {
      addMessage(new ErrorMessage('Unable to update the position.'));
    }
  };

  const drawer = (() => {
    if (!positionSlot) return undefined;
    if (activeDrawer === ActiveDrawer.EDIT) {
      return (
        <PositionSlotFormDrawer
          isOpen
          positionSlot={positionSlot}
          onClose={() => setActiveDrawer(null)}
          onSuccess={() => reload()}
        />
      );
    }
    if (activeDrawer === ActiveDrawer.PFR) {
      return (
        <CreatePositionFillRequestDrawer
          isOpen
          positionSlot={positionSlot}
          onClose={() => setActiveDrawer(null)}
          onCreated={() => reload()}
        />
      );
    }
    if (activeDrawer === ActiveDrawer.ASSIGN) {
      return (
        <PositionSlotAssignmentDrawer
          isOpen
          positionSlot={positionSlot}
          onClose={() => setActiveDrawer(null)}
          onSuccess={() => reload()}
        />
      );
    }
    return undefined;
  })();

  return (
    <PositionSlotValidationProvider positionSlots={positionSlot ? [positionSlot] : []} loading={isLoading}>
      <AddressableViewLayout
        loading={isLoading}
        contentLayout={
          <PositionSlotDetailsContent
            positionSlot={positionSlot}
            loading={isLoading}
            error={error}
            retry={reload}
            callbacks={{
              onEdit: () => setActiveDrawer(ActiveDrawer.EDIT),
              onAssign: () => setActiveDrawer(ActiveDrawer.ASSIGN),
              onCreatePositionFillRequest: () => setActiveDrawer(ActiveDrawer.PFR),
              onFreeze: () =>
                positionSlot &&
                runAction(() => service.freezePositionSlot(positionSlot.id), 'Position has been successfully frozen.'),
              onUnfreeze: () =>
                positionSlot &&
                runAction(
                  () => service.unfreezePositionSlot(positionSlot.id),
                  'Position has been successfully unfrozen.',
                ),
              onRetire: () =>
                positionSlot &&
                runAction(() => service.retirePositionSlot(positionSlot.id), 'Position has been successfully retired.'),
            }}
            permissions={{
              canUpdate: isGranted(POSITION_SLOT_UPDATE),
              canCreatePositionFillRequest:
                isGranted(POSITION_FILL_REQUEST_CREATE) && isFeatureEnabled(Features.POSITION_FILL_REQUESTS_COLLECTION),
              canAssign: isGranted(POSITION_SLOT_ASSIGN),
              canFreeze: isGranted(POSITION_SLOT_FREEZE),
              canUnfreeze: isGranted(POSITION_SLOT_UNFREEZE),
              canRetire: isGranted(POSITION_SLOT_RETIRE),
              // TODO Update this permission to a more appropriate one when it is available
              canCreateTimelineEntry: isGranted(POSITION_SLOT_UPDATE),
            }}
          />
        }
        drawer={drawer}
      />
    </PositionSlotValidationProvider>
  );
};

export default PositionSlotDetailsPage;
